import { logger } from 'lib/hatchd-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { AuthPageLayout } from 'components/auth-page-layout/auth-page-layout';
import { Layout } from 'components/layout';
import {
  Button,
  Grid,
  Heading,
  Icon,
  Input,
  Message,
  Stack,
  Text,
} from 'components/ui';
import { ApiClient } from 'lib/api/client';
import { UserRegisterBuyerRequest } from 'lib/api/generated';
import { getFormFieldErrorsFromResponse } from 'lib/helpers';
import { NextPage } from 'next';
import { useRouter } from 'next/dist/client/router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PATHS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import * as yup from 'yup';
import { globalTheme } from '../../styles/global-theme';
import { Link } from '../../components/link';

interface SignUpPageProps {}

type FormData = UserRegisterBuyerRequest;

const StyledHeading = styled(Heading)`
  display: block;
  font-weight: 600;
  font-size: 64px;

  @media screen and (max-width: ${breakpoints.medium}) {
    display: none;
  }

  @media screen and (max-width: ${breakpoints.xLarge}) {
    font-size: 48px;
  }
`;

const ButtonContainer = styled.div`
  display: 'flex';
  justify-content: 'space-between';
`;

const LinkWrap = styled.div`
  padding-top: ${globalTheme.space[0]};
`;

const schema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  password: yup.string().required('Please enter a password'),
  confirm_password: yup
    .string()
    .test('passwords-match', 'Passwords must match', function (value) {
      return this.parent.password === value;
    }),
  firstname: yup.string().required('Please enter your name'),
  lastname: yup.string().required('Please enter your last name'),
  mobile: yup.string().required('Please enter your mobile number'),
  phone: yup.string().nullable(),
});

const SignUpPage: NextPage<SignUpPageProps> = () => {
  const router = useRouter();

  const mutation = useMutation(
    (userRegisterBuyerRequest: UserRegisterBuyerRequest) =>
      new ApiClient().registerApi.userRegisterCreate({
        userRegisterBuyerRequest,
      })
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const onSubmit = async ({ phone: phoneData, mobile, ...data }: FormData) => {
    // Set empty phone to undefined so these values aren't included in the data sent to the API
    const phone = phoneData || undefined;

    try {
      await mutation.mutateAsync({
        ...data,
        phone,
        mobile,
      });

      router.push(PATHS.auth.verification);
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      if (error.response) {
        // Handle server errors
        if (error.response.status === 500) {
          logger.error('500 Error');
          toast.error('Something has gone wrong, please try again later');
        }

        // Handle validation errors
        if (error.response.data.code === 'VALIDATION_ERROR') {
          logger.error('Validation error');

          // Map errors returned to relevant form fields
          const errors = getFormFieldErrorsFromResponse<FormData>(
            error.response.data.errors
          );
          errors.forEach(({ field, message }) => setError(field, { message }));
        }
      }
    }
  };

  const handleBackToLogin = () => {
    router.push(PATHS.auth.logIn);
  };

  return (
    <Layout meta={{ title: 'Sign up' }}>
      <AuthPageLayout>
        <Stack space={3}>
          <StyledHeading level='h1'>
            Create an Agora Livestock account
          </StyledHeading>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack space={2}>
              <Grid columns={2} stackOnSmall gap={2}>
                <Input
                  id='firstname'
                  type='firstname'
                  label='First name'
                  placeholder='Calvin'
                  state={errors.firstname && 'error'}
                  message={errors.firstname?.message}
                  {...register('firstname')}
                  data-cy='firstNameSignupField'
                />
                <Input
                  id='lastname'
                  type='lastname'
                  label='Last name'
                  placeholder='Broadus'
                  state={errors.lastname && 'error'}
                  message={errors.lastname?.message}
                  {...register('lastname')}
                  data-cy='lastNameSignupField'
                />
              </Grid>
              <Input
                id='email'
                type='email'
                label='Email'
                placeholder='calvin@email.com'
                state={errors.email && 'error'}
                message={errors.email?.message}
                {...register('email')}
                data-cy='emailSignupField'
              />
              <Grid columns={2} stackOnSmall gap={2}>
                <Input
                  id='mobile'
                  type='mobile'
                  label='Mobile'
                  placeholder='0451 553 453'
                  state={errors.mobile && 'error'}
                  message={errors.mobile?.message}
                  {...register('mobile')}
                  data-cy='mobileSignupField'
                />
                <Input
                  id='phone'
                  type='phone'
                  label='Phone'
                  placeholder='9733 3737'
                  state={errors.phone && 'error'}
                  message={errors.phone?.message}
                  {...register('phone')}
                  data-cy='phoneSignupField'
                />
              </Grid>
              <Message backgroundColor='neutralLight'>
                <Grid columns='min-content 1fr' gap={1} alignItems='center'>
                  <Icon component='AlertIcon' size={2} />
                  <Text fontSize={0} textAlign='left'>
                    Please make sure your email address and mobile number are
                    accurate for verification purposes.
                  </Text>
                </Grid>
              </Message>
              <Grid columns={2} stackOnSmall gap={2}>
                <Input
                  id='password'
                  type='password'
                  label='Create password'
                  placeholder='********'
                  state={errors.password && 'error'}
                  message={errors.password?.message}
                  {...register('password')}
                  data-cy='passwordSignupField'
                />
                <Input
                  id='confirm_password'
                  type='password'
                  label='Confirm password'
                  placeholder='********'
                  state={errors.confirm_password && 'error'}
                  message={errors.confirm_password?.message}
                  {...register('confirm_password')}
                  data-cy='confirmPasswordSignupField'
                />
              </Grid>
              <ButtonContainer>
                <Button
                  type='submit'
                  isLoading={isSubmitting}
                  data-cy='createAccountButton'
                >
                  Create account
                </Button>
              </ButtonContainer>
              <LinkWrap>
                <Link href={PATHS.auth.logIn}>
                  <a data-cy='createAccountButton'>Back to login</a>
                </Link>
              </LinkWrap>
            </Stack>
          </form>
        </Stack>
      </AuthPageLayout>
    </Layout>
  );
};

export default SignUpPage;
